import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { HomeSections } from "@/components/home/HomeSections";
import { useAuth } from "@/contexts/AuthContext";

/**
 * The real tenant homepage (navbar, sections, footer) for the signed-in owner's
 * business, rendered inside the Theme page's preview iframes.
 *
 * The theme is forced by `ThemeProvider` from the query string
 * (`?preset=…&hf=…&bf=…&bs=…&pc=…&bt=…`) for this document only — nothing is
 * read from or written to `theme_settings`. OrgProvider resolves this path to
 * the owner's own business. The page is `noindex`, the intro veil and admin bar
 * are skipped by Layout, and links/forms are inert so the preview can't navigate
 * or submit anything.
 */
export default function ThemePreviewFrame() {
  const { user, loading, rolesLoading } = useAuth();

  useEffect(() => {
    const stop = (event: Event) => {
      const target = event.target as Element | null;
      if (event.type === "submit" || target?.closest("a[href], button[type='submit']")) event.preventDefault();
    };
    document.addEventListener("click", stop, true);
    document.addEventListener("submit", stop, true);
    return () => {
      document.removeEventListener("click", stop, true);
      document.removeEventListener("submit", stop, true);
    };
  }, []);

  // Wait for the owner's membership too, so no query ever runs against the fallback business.
  if (loading || (user && rolesLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center text-sm text-muted-foreground">
        Sign in to see your theme preview.
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Theme preview</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Layout>
        <HomeSections />
      </Layout>
    </>
  );
}
